/**
 * ResultsTabContent — contenido de la pestaña "Resultados" en RacesPage
 * (v16.3.1 R.1 + v16.4.1 §T4).
 *
 * Particiona el array `races` en dos buckets ordenados:
 *   1. Pendientes de reportar (race_date <= today AND !completed).
 *   2. Resultados guardados (completed=true).
 *
 * Carreras FUTURAS (race_date > today) NO aparecen en esta pestaña —
 * su entry point sigue siendo la pestaña principal "Mis carreras".
 *
 * Aritmética de fecha en UTC pura (patrón anclado del repo, ver
 * RaceCard `_todayIsoUtc`) — evita TZ drift al comparar contra
 * `race.race_date` que también es ISO date sin TZ.
 */
import { t } from "@/i18n/es";
import type { RaceTarget } from "@/types/api";

import { RaceResultListItem } from "./RaceResultListItem";
import { RaceResultSummary } from "./RaceResultSummary";

export interface ResultsTabContentProps {
  races: RaceTarget[];
  onAddResult: (id: number) => void;
}

function _todayIsoUtc(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(
    2,
    "0",
  )}-${String(now.getUTCDate()).padStart(2, "0")}`;
}

export function ResultsTabContent({
  races,
  onAddResult,
}: ResultsTabContentProps) {
  const today = _todayIsoUtc();
  const pendingRaces = races.filter(
    (r) => r.race_date <= today && !r.completed,
  );
  const recordedRaces = races.filter((r) => r.completed === true);

  // Empty-empty (no hay ni pendientes ni guardadas).
  const isCompletelyEmpty = pendingRaces.length === 0 && recordedRaces.length === 0;

  if (isCompletelyEmpty) {
    return (
      <section
        data-testid="results-tab-empty"
        className="flex flex-col items-center gap-4 py-8 text-center"
      >
        <p className="text-sm text-bone-muted">{t.races.resultsEmptyAll}</p>
      </section>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="results-tab-content">
      <section
        data-testid="results-tab-pending-section"
        aria-label={t.races.resultsPendingSectionTitle}
        className="flex flex-col gap-2"
      >
        <h2 className="text-xs uppercase tracking-widest text-gray-mid">
          {t.races.resultsPendingSectionTitle}
        </h2>
        {pendingRaces.length === 0 ? (
          <p
            data-testid="results-tab-pending-empty"
            className="text-sm text-bone-muted"
          >
            {t.races.resultsEmptyPending}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {pendingRaces.map((race) => (
              <RaceResultListItem
                key={race.id}
                race={race}
                onAddResult={onAddResult}
              />
            ))}
          </div>
        )}
      </section>

      <section
        data-testid="results-tab-recorded-section"
        aria-label={t.races.resultsRecordedSectionTitle}
        className="flex flex-col gap-2"
      >
        <h2 className="text-xs uppercase tracking-widest text-gray-mid">
          {t.races.resultsRecordedSectionTitle}
        </h2>
        {recordedRaces.length === 0 ? (
          <p
            data-testid="results-tab-recorded-empty"
            className="text-sm text-bone-muted"
          >
            {t.races.resultsEmptyRecorded}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {recordedRaces.map((race) => (
              <RaceResultSummary key={race.id} race={race} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
